import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { NavBar } from '../components/NavBar';
import {
  CheckCircle2, Loader2, Sparkles, Utensils, ShoppingCart,
  Shuffle, Star, Crown, ArrowRight, Lock
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const GOALS = [
  { id: 'weight_loss', label: 'Weight Loss' },
  { id: 'muscle_building', label: 'Muscle Building' },
  { id: 'endurance', label: 'Endurance' },
  { id: 'general_fitness', label: 'General Fitness' },
  { id: 'strength', label: 'Strength' },
];

const TIER_ORDER = ['4w', '8w', '12w'];
const TIER_META = {
  '4w':  { label: '4 Weeks',  sub: '1 month',   savings: null,        regularPrice: null },
  '8w':  { label: '8 Weeks',  sub: '2 months',  savings: 'Save $25',  regularPrice: 300 },
  '12w': { label: '12 Weeks', sub: '3 months',  savings: 'Save $51',  regularPrice: 450, highlight: true },
};

const FEATURES = [
  { icon: Utensils, text: 'Full AI meal plan with daily macros' },
  { icon: ShoppingCart, text: 'Auto-generated weekly grocery lists' },
  { icon: Shuffle, text: 'Swap any meal with AI alternatives' },
  { icon: Star, text: 'Save favorites — influences future plans' },
];

export const NutritionPlansPurchasePage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();

  const [tiers, setTiers] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [loading, setLoading] = useState(true);

  // Free users (non-admin) can browse the page but cannot buy.
  // Admins are always allowed.
  const isAdmin = !!user?.is_admin;
  const premiumLocked = !isPremium && !isAdmin;

  const [selectedTier, setSelectedTier] = useState('8w');
  const [goal, setGoal] = useState('general_fitness');
  const [mealsPerDay, setMealsPerDay] = useState(4);
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    const fetchTiers = async () => {
      try {
        const res = await api().get('/nutrition-plans/purchase/tiers');
        setTiers(res.data.tiers);
        setIsPremium(res.data.is_premium);
      } catch {
        toast.error('Failed to load pricing');
      }
      setLoading(false);
    };
    fetchTiers();
  }, [api]);

  const handleCheckout = async () => {
    if (premiumLocked) {
      navigate('/subscription');
      return;
    }
    setCheckingOut(true);
    try {
      const res = await api().post('/nutrition-plans/purchase/create-checkout', {
        tier: selectedTier,
        goal,
        meals_per_day: mealsPerDay,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Failed to start checkout');
      }
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error('Nutrition plans are exclusive to premium members.');
        navigate('/subscription');
      } else {
        toast.error(err.response?.data?.detail || 'Checkout failed');
      }
    }
    setCheckingOut(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <NavBar />
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-12 w-12 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="nutrition-plans-page">
      <NavBar />

      <main className="container mx-auto px-6 py-10">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12 max-w-3xl mx-auto">
          <Badge className="bg-green-500/20 text-green-400 border-green-500/30 mb-4">
            <Sparkles className="h-3 w-3 mr-1" /> Personalized Nutrition
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-wider mb-4">
            Get Your <span className="text-primary">Custom Meal Plan</span>
          </h1>
          <p className="text-muted-foreground text-lg">
            AI-generated meal plans built around your goal, macros, and dietary preferences — delivered instantly.
          </p>
          {isPremium && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded">
              <Crown className="h-4 w-4 text-yellow-400" />
              <span className="text-yellow-400 text-sm font-medium">Premium perk: 20% off all plans</span>
            </div>
          )}
        </motion.div>

        {/* Premium-required banner for free users */}
        {premiumLocked && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto mb-10 bg-gradient-to-br from-yellow-500/15 to-yellow-500/5 border-2 border-yellow-500/40 rounded-2xl p-5 flex items-start gap-4"
            data-testid="premium-required-banner"
          >
            <div className="h-10 w-10 rounded bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
              <Lock className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-white uppercase tracking-wider mb-1">
                Premium members only
              </p>
              <p className="text-white/70 text-sm">
                Nutrition plans are exclusive to premium subscribers. Unlock unlimited AI meal plans, grocery lists, meal swaps, and more.
              </p>
            </div>
            <Link to="/subscription" className="flex-shrink-0">
              <Button className="bg-yellow-500 text-black font-heading tracking-wider uppercase hover:bg-yellow-400" data-testid="unlock-premium-btn">
                <Crown className="h-4 w-4 mr-2" /> Unlock
              </Button>
            </Link>
          </motion.div>
        )}

        {/* Pricing tiers */}
        <div className="grid md:grid-cols-3 gap-6 mb-10 max-w-5xl mx-auto">
          {TIER_ORDER.map((key, idx) => {
            const tier = tiers?.[key];
            const meta = TIER_META[key];
            if (!tier) return null;
            const selected = selectedTier === key;
            return (
              <motion.button
                key={key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.08 }}
                onClick={() => setSelectedTier(key)}
                className={`relative text-left bg-card border-2 p-6 transition-all ${
                  selected ? 'border-primary shadow-[0_0_20px_rgba(250,204,21,0.2)]' : 'border-border hover:border-primary/50'
                }`}
                data-testid={`tier-${key}`}
              >
                {meta.highlight && (
                  <Badge className="absolute -top-3 left-6 bg-primary text-black font-heading tracking-wider">Best Value</Badge>
                )}
                <h3 className="font-heading text-2xl text-white uppercase tracking-wider mb-1">{meta.label}</h3>
                <p className="text-muted-foreground text-sm mb-4">{meta.sub}</p>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="font-heading text-4xl text-primary">${tier.final_price.toFixed(2)}</span>
                  {isPremium && meta.regularPrice && (
                    <span className="text-muted-foreground line-through text-sm">${tier.base_price.toFixed(2)}</span>
                  )}
                </div>
                {meta.regularPrice && !isPremium && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground line-through">${meta.regularPrice}</span>
                    <span className="text-green-400 font-medium">{meta.savings}</span>
                  </div>
                )}
                {isPremium && (
                  <p className="text-yellow-400 text-xs mt-1">20% Premium discount applied</p>
                )}
                {selected && (
                  <div className="mt-4 pt-4 border-t border-border flex items-center gap-2 text-primary text-sm font-medium">
                    <CheckCircle2 className="h-4 w-4" /> Selected
                  </div>
                )}
              </motion.button>
            );
          })}
        </div>

        {/* Customization */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="max-w-3xl mx-auto bg-card border border-border p-6 space-y-6"
        >
          <h3 className="font-heading text-xl text-white uppercase tracking-wider">Customize your plan</h3>

          <div>
            <label className="block text-xs text-muted-foreground uppercase tracking-wider mb-2">Primary Goal</label>
            <div className="flex flex-wrap gap-2">
              {GOALS.map((g) => (
                <button
                  key={g.id}
                  onClick={() => setGoal(g.id)}
                  className={`px-3 py-2 border text-sm transition-colors ${
                    goal === g.id ? 'bg-primary text-black border-primary' : 'border-border text-white hover:border-primary/50'
                  }`}
                  data-testid={`goal-${g.id}`}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs text-muted-foreground uppercase tracking-wider mb-2">Meals per day</label>
            <div className="flex gap-2">
              {[3, 4, 5, 6].map((n) => (
                <button
                  key={n}
                  onClick={() => setMealsPerDay(n)}
                  className={`h-12 w-12 border text-sm font-heading transition-colors ${
                    mealsPerDay === n ? 'bg-primary text-black border-primary' : 'border-border text-white hover:border-primary/50'
                  }`}
                  data-testid={`meals-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {(user?.dietary_restrictions?.length > 0 || user?.custom_dietary_notes) ? (
            <div className="bg-black border border-green-500/20 p-3 rounded">
              <p className="text-xs text-green-400 font-heading uppercase tracking-wider mb-2">Your dietary restrictions will be applied:</p>
              <div className="flex flex-wrap gap-1.5">
                {(user?.dietary_restrictions || []).map((r) => (
                  <Badge key={r} variant="outline" className="border-green-500/40 text-green-300 text-xs">{r.replace('_', ' ')}</Badge>
                ))}
                {user?.custom_dietary_notes && (
                  <Badge variant="outline" className="border-green-500/40 text-green-300 text-xs">{user.custom_dietary_notes}</Badge>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-black border border-border p-3 rounded text-sm">
              Have allergies or a specific diet?{' '}
              <Link to="/profile" className="text-green-400 underline">Add restrictions on your profile</Link> — they'll be applied automatically.
            </div>
          )}
        </motion.div>

        {/* What's included */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="max-w-3xl mx-auto mt-6 bg-card border border-border p-6"
        >
          <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-4">What's Included</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {FEATURES.map((f) => (
              <div key={f.text} className="flex items-start gap-3">
                <div className="h-9 w-9 rounded bg-green-500/10 flex items-center justify-center flex-shrink-0">
                  <f.icon className="h-4 w-4 text-green-400" />
                </div>
                <p className="text-white text-sm pt-2">{f.text}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Checkout button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="max-w-3xl mx-auto mt-6 flex items-center justify-between gap-4 bg-primary/5 border border-primary/30 p-6"
        >
          <div>
            <p className="text-white text-sm">You're purchasing:</p>
            <p className="font-heading text-xl text-white">
              {TIER_META[selectedTier].label} · <span className="text-primary">${tiers?.[selectedTier]?.final_price?.toFixed(2)}</span>
            </p>
          </div>
          {premiumLocked ? (
            <Button
              size="lg"
              onClick={() => navigate('/subscription')}
              className="bg-yellow-500 text-black font-heading tracking-wider uppercase hover:bg-yellow-400"
              data-testid="checkout-btn"
            >
              <Lock className="h-4 w-4 mr-2" /> Unlock With Premium
            </Button>
          ) : (
            <Button
              size="lg"
              onClick={handleCheckout}
              disabled={checkingOut}
              className="bg-primary text-black font-heading tracking-wider uppercase hover:bg-primary/90"
              data-testid="checkout-btn"
            >
              {checkingOut ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting...</>
              ) : (
                <>Secure Checkout <ArrowRight className="h-4 w-4 ml-2" /></>
              )}
            </Button>
          )}
        </motion.div>

        <p className="text-muted-foreground text-xs text-center mt-4">
          {premiumLocked
            ? 'Nutrition plans require an active premium membership'
            : 'Secured by Stripe · You\'ll be redirected to complete payment'}
        </p>
      </main>
    </div>
  );
};
